import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { formatMoney, maskMoneyInput, parseInputToApi, moneyToInput } from '@/lib/money';
import { Modal, Field } from '@/features/transactions/TransactionForm';
import type { ReferenceData } from '@/features/transactions/types';

export type EditableAccount = {
  id: number;
  name: string;
  type: string;
  entity_id: number;
  bank_id?: number | null;
  branch?: string | null;
  holder_name?: string | null;
  color: string | null;
  opening_balance: string;
  opening_balance_date: string | null;
  overdraft_limit: string;
  notes?: string | null;
};

const TYPES = [
  { value: 'corrente', label: 'Conta corrente' },
  { value: 'poupanca', label: 'Poupança' },
  { value: 'digital', label: 'Conta digital' },
  { value: 'pagamento', label: 'Conta de pagamento' },
  { value: 'investimento', label: 'Investimento' },
  { value: 'caixa_fisico', label: 'Caixa físico' },
  { value: 'carteira', label: 'Carteira' },
];

const COLORS = ['#2563eb', '#059669', '#7c3aed', '#db2777', '#ea580c', '#0891b2', '#64748b'];

export function AccountForm({
  account,
  reference,
  selectedIds,
  onClose,
  onSaved,
}: {
  account: EditableAccount | null;
  reference: ReferenceData;
  selectedIds: number[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = account !== null;
  const today = new Date().toISOString().slice(0, 10);

  const [name, setName] = useState(account?.name ?? '');
  const [type, setType] = useState(account?.type ?? 'corrente');
  const [entityId, setEntityId] = useState(account?.entity_id ?? reference.entities[0]?.id ?? 0);
  const [bankId, setBankId] = useState<string>(String(account?.bank_id ?? ''));
  const [branch, setBranch] = useState(account?.branch ?? '');
  const [openingMasked, setOpeningMasked] = useState(
    account ? moneyToInput(account.opening_balance.replace('-', '')) : '',
  );
  const [openingNegative, setOpeningNegative] = useState(Number(account?.opening_balance ?? 0) < 0);
  const [openingDate, setOpeningDate] = useState(account?.opening_balance_date ?? today);
  const [overdraftMasked, setOverdraftMasked] = useState(
    account && account.overdraft_limit !== '0.0000'
      ? moneyToInput(account.overdraft_limit)
      : '',
  );
  const [color, setColor] = useState(account?.color ?? COLORS[0]!);

  const opening = (openingNegative ? '-' : '') + parseInputToApi(openingMasked || '0');
  const overdraft = parseInputToApi(overdraftMasked || '0');

  // Comparação só para decidir se mostra o aviso — nunca para calcular.
  // A confirmação real do impacto vem do backend, que conta os movimentos.
  const openingChanged =
    isEdit &&
    (Number(opening) !== Number(account.opening_balance) ||
      openingDate !== account.opening_balance_date);

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name,
        type,
        ...(isEdit ? {} : { entity_id: entityId }),
        ...(bankId ? { bank_id: Number(bankId) } : {}),
        ...(branch ? { branch } : {}),
        opening_balance: opening,
        opening_balance_date: openingDate,
        overdraft_limit: overdraft,
        color,
      };

      return isEdit
        ? api.patch<{ meta?: { warning: string | null } }>(`/accounts/${account.id}`, payload, {
            entityIds: selectedIds,
          })
        : api.post(`/accounts`, payload, { entityIds: selectedIds });
    },
    onSuccess: (res) => {
      const warning = (res as { meta?: { warning?: string | null } })?.meta?.warning;
      if (warning) window.alert(warning);
      onSaved();
    },
  });

  const error = save.error instanceof ApiError ? save.error : null;
  const canSubmit = name.trim().length >= 2 && entityId > 0 && openingDate !== '';

  return (
    <Modal title={isEdit ? 'Editar conta' : 'Nova conta bancária'} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-4">
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300"
          >
            <p>{error.message}</p>
            {error.errors && (
              <ul className="mt-1 list-inside list-disc">
                {Object.entries(error.errors).map(([f, m]) => <li key={f}>{m[0]}</li>)}
              </ul>
            )}
          </div>
        )}

        <Field label="Nome da conta" required>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            minLength={2}
            autoFocus
            placeholder="Ex.: Empresa · Itaú"
            className="input w-full"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Tipo" required>
            <select value={type} onChange={(e) => setType(e.target.value)} className="input w-full">
              {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>

          <Field
            label="Entidade"
            required
            hint={isEdit ? 'não pode ser alterada' : undefined}
          >
            <select
              value={entityId}
              onChange={(e) => setEntityId(Number(e.target.value))}
              disabled={isEdit}
              className="input w-full disabled:opacity-60"
            >
              {reference.entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Banco">
            <select value={bankId} onChange={(e) => setBankId(e.target.value)} className="input w-full">
              <option value="">Nenhum</option>
              {reference.banks.map((b) => (
                <option key={b.id} value={b.id}>{b.code} — {b.short_name ?? b.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Agência">
            <input value={branch} onChange={(e) => setBranch(e.target.value)} className="input w-full" />
          </Field>
        </div>

        {/* O par mais delicado do formulário — RN-80 e RF-ENT-08 */}
        <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
          <p className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-slate-400">
            Saldo inicial
          </p>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Valor" required>
              <div className="flex gap-1.5">
                <button
                  type="button"
                  onClick={() => setOpeningNegative((v) => !v)}
                  className={`w-9 rounded-lg border text-sm font-medium transition ${
                    openingNegative
                      ? 'border-rose-500 bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300'
                      : 'border-slate-300 text-slate-500 dark:border-slate-700'
                  }`}
                  title="Saldo negativo (conta no vermelho)"
                >
                  {openingNegative ? '−' : '+'}
                </button>
                <input
                  value={openingMasked}
                  onChange={(e) => setOpeningMasked(maskMoneyInput(e.target.value))}
                  inputMode="numeric"
                  placeholder="0,00"
                  className="input w-full text-right"
                />
              </div>
            </Field>
            <Field label="Nesta data" required>
              <input
                type="date"
                value={openingDate}
                onChange={(e) => setOpeningDate(e.target.value)}
                required
                max={today}
                className="input w-full"
              />
            </Field>
          </div>

          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            Informe o saldo que a conta tinha nesta data. Só o que veio depois precisa ser lançado —
            você não precisa importar anos de histórico para começar.
          </p>

          {openingChanged && (
            <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              Alterar o saldo inicial desloca <strong>todo</strong> o saldo desta conta, inclusive
              períodos já conciliados com o extrato do banco.
            </p>
          )}
        </div>

        <Field label="Limite do cheque especial" hint="entra no saldo disponível, não no saldo">
          <input
            value={overdraftMasked}
            onChange={(e) => setOverdraftMasked(maskMoneyInput(e.target.value))}
            inputMode="numeric"
            placeholder="0,00"
            className="input w-full text-right"
          />
        </Field>

        <div>
          <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Cor</span>
          <div className="flex gap-2">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                style={{ background: c }}
                className={`h-7 w-7 rounded-lg transition ${
                  color === c ? 'ring-2 ring-slate-900 ring-offset-2 dark:ring-white dark:ring-offset-slate-900' : ''
                }`}
                aria-label={`Cor ${c}`}
              />
            ))}
          </div>
        </div>

        {Number(opening) !== 0 && (
          <p className="rounded-lg bg-slate-50 px-3.5 py-2.5 text-sm dark:bg-slate-800/50">
            Saldo inicial: <strong>{formatMoney(opening)}</strong>
            {Number(overdraft) > 0 && (
              <> · disponível com limite: <strong>{formatMoney((Number(opening) + Number(overdraft)).toFixed(2))}</strong></>
            )}
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSubmit || save.isPending}
            className="flex-1 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
